// import { useEffect } from 'react';

import { useEffect } from 'react';

import { useLocation, useNavigate } from 'react-router';

import { useAtomValue, useSetAtom } from 'jotai';
import { focusAtom } from 'jotai-optics';
import { useTranslation } from 'react-i18next';

import { sendNativeMessage } from '@/hooks/useNativeListener';

import { cn } from '@/utils/tailwind';
import { useRouteMatcher } from '@/utils/url';

import ConfirmationModal from '@/components/popups/ConfirmationModal';

import Constants, { routes } from '@/constants';

import { LocationState } from '@/types/types';

import { store } from '@/atoms';
import {
  trainingModeAtom,
  trainingModeDispatchAtom,
} from '@/atoms/trainingMode';

interface TrainingModeWrapperProps {
  children: React.ReactNode;
}

const isTrainingModeAtom = focusAtom(trainingModeAtom, o =>
  o.prop('isTrainingMode'),
);

const activePlanIdAtom = focusAtom(trainingModeAtom, o =>
  o.prop('activePlanId'),
);

const TrainingModeWrapper: React.FC<TrainingModeWrapperProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const routeMatches = useRouteMatcher();
  const isTrainingMode = useAtomValue(isTrainingModeAtom);
  const sendTrainingModeAction = useSetAtom(trainingModeDispatchAtom);

  const locState = location.state as LocationState | undefined;

  const isTrainingModeView =
    routeMatches.includes(Constants.ROUTE_PATH_GROUPS.TrainingModePath) ||
    routeMatches.includes(Constants.ROUTE_PATH_GROUPS.TrainingToCompletePath);

  useEffect(() => {
    if (locState?.toTrainingMode) {
      sendTrainingModeAction('enterTrainingMode');
    }
  }, [sendTrainingModeAction, locState?.toTrainingMode]);

  const backToTrainingMode = () => {
    navigate({
      pathname: routes.plans.trainTogether({
        planId: store.get(activePlanIdAtom) ?? '',
      }),
    });
  };

  const onExitTrainingMode = () => {
    sendTrainingModeAction('exitTrainingMode');
    sendTrainingModeAction('resetTrainingModeData');

    if (window.native.isNative) {
      sendNativeMessage({ type: 'trainingModeActive', payload: false });
      sendNativeMessage({ type: 'navigateTo', payload: '/' });
      return;
    }

    navigate({
      pathname: '/',
    });
  };

  const shouldOpenExitTrainingModeModal = isTrainingMode && !isTrainingModeView;

  return (
    <div
      className={cn(
        isTrainingMode &&
          'fixed top-0 left-0 size-full overflow-auto border-8 border-[#3EB1C8]',
      )}
    >
      <span className={cn(isTrainingMode && !isTrainingModeView && 'blur-xl')}>
        {children}
      </span>
      <ConfirmationModal
        bodyText={t('TrainingPlans.trainTogether.exitModalBody')}
        headerText={t('TrainingPlans.trainTogether.exitModalHeader')}
        isOpen={shouldOpenExitTrainingModeModal}
        primaryButtonColor={'secondary'}
        primaryButtonHandler={onExitTrainingMode}
        primaryButtonText={t('Button.exit')}
        secondaryButtonHandler={backToTrainingMode}
        secondaryButtonText={t('Button.backToTrainingMode')}
        showCloseButton={false}
      />
    </div>
  );
};

export default TrainingModeWrapper;
